import React, { useState } from "react";
import { useFormik } from 'formik';
import * as Yup from 'yup';
import PTCStepper from '../../../components/GenericComponents/PTCStepper';
import PTInput from "../../../components/GenericComponents/PTInput";
import PTButton from "../../../components/GenericComponents/PTButton";
import { Steps } from "../../constants/Stepper";
import { LabelStyle, SnapshotQuestions2, TooltipText } from "../../constants/General";
import { useSelector } from "react-redux";
import { sendHeading2Data } from "../../../utils/middleware/Heading2";
import InfoIcon from "../../../components/InfoIcon";

function Heading2({ setActiveStep }) {
  // Store
  const { companyDetails } = useSelector(state => state.proptechUser);

  // State
  const [handleInfo, setHandleInfo] = useState([])

  const initialValues = {
    investability: companyDetails?.investability,
    teamStrength: companyDetails?.teamStrength,
    KPIs: companyDetails?.KPIs,
  };

  // Handle form values
  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: Yup.object({
      investability: Yup.string().trim().min(100, "Must be 100 characters or more").max(400, "Must be 400 characters or less").required('Required'),
      teamStrength: Yup.string().trim().min(100, "Must be 100 characters or more").max(400, "Must be 400 characters or less").required('Required'),
      KPIs: Yup.string().trim().min(100, "Must be 100 characters or more").max(400, "Must be 400 characters or less").required('Required'),
    }),
    onSubmit: (values, { resetForm }) => {
      if (companyDetails?.isPrimaryUser) {
        sendHeading2Data(
          Object.fromEntries(Object.entries(values).filter(([_, v]) => v !== '')),
          companyDetails,
          setActiveStep,
          resetForm)
      } else { setActiveStep(3) }
    }
  });

  return (
    <div className="p-16 heading1-block-wrapper flex flex-col justify-between rounded-3xl bg-[#FFFFFF] right-step-wrapper boxshadow-box">
      <form onSubmit={formik.handleSubmit}>
        <div className="font-light text-4xl main-title not-italic">Company overview 2</div>
        <section>
          <div className="md:mt-14 mt-10">
            <PTInput
              className="has-icon-field"
              labelStyle={LabelStyle}
              variant="standard"
              name="investability"
              shrink
              label={SnapshotQuestions2.investability}
              fullWidth
              multiline
              maxRows={4}
              maxLength={400}
              disabled={!companyDetails?.isPrimaryUser}
              icon={
                <InfoIcon
                  title='investability'
                  label={TooltipText.investability}
                  info={handleInfo}
                  handleTooltipClose={(v) => setHandleInfo(handleInfo.filter((i) => handleInfo.indexOf(i) === -1))}
                  handleTooltipOpen={(v) => setHandleInfo([...handleInfo, v])}
                  className="info-icon"
                />
              }
              error={formik.touched.investability && formik.errors.investability}
              handleOnChangeEvent={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.investability} />
            <div className="mt-2 flex justify-between items-center">
              <span className='font-normal text-xs text-left tracking-wide text-[#d32f2f]'>{formik.touched.investability && formik.errors.investability}</span>
              <span className='text-xs font-semibold' style={{ color: 'rgba(17, 29, 67, 0.4)' }}>{formik.values.investability?.trim()?.length}/<span>400</span> CHARACTERS</span>
            </div>
          </div>
          <div className="md:mt-14 mt-10">
            <PTInput
              className="has-icon-field"
              labelStyle={LabelStyle}
              variant="standard"
              name="teamStrength"
              shrink
              label={SnapshotQuestions2.teamStrength}
              fullWidth
              multiline
              maxRows={4}
              maxLength={400}
              disabled={!companyDetails?.isPrimaryUser}
              icon={
                <InfoIcon
                  title='teamStrength'
                  label={TooltipText.teamStrength}
                  info={handleInfo}
                  handleTooltipClose={(v) => setHandleInfo(handleInfo.filter((i) => handleInfo.indexOf(i) === -1))}
                  handleTooltipOpen={(v) => setHandleInfo([...handleInfo, v])}
                  className="info-icon"
                />
              }
              error={formik.touched.teamStrength && formik.errors.teamStrength}
              handleOnChangeEvent={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.teamStrength} />
            <div className="mt-2 flex justify-between items-center">
              <span className='font-normal text-xs text-left tracking-wide text-[#d32f2f]'>{formik.touched.teamStrength && formik.errors.teamStrength}</span>
              <span className='text-xs font-semibold' style={{ color: 'rgba(17, 29, 67, 0.4)' }}>{formik.values.teamStrength?.trim()?.length}/<span>400</span> CHARACTERS</span>
            </div>
          </div>
          <div className="md:mt-14 mt-10">
            <PTInput
              className="has-icon-field"
              labelStyle={LabelStyle}
              variant="standard"
              name="KPIs"
              shrink
              label={SnapshotQuestions2.KPIs}
              fullWidth
              multiline
              maxRows={4}
              maxLength={400}
              disabled={!companyDetails?.isPrimaryUser}
              icon={
                <InfoIcon
                  title='KPIs'
                  label={TooltipText.KPIs}
                  info={handleInfo}
                  handleTooltipClose={(v) => setHandleInfo(handleInfo.filter((i) => handleInfo.indexOf(i) === -1))}
                  handleTooltipOpen={(v) => setHandleInfo([...handleInfo, v])}
                  className="info-icon"
                />
              }
              error={formik.touched.KPIs && formik.errors.KPIs}
              handleOnChangeEvent={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.KPIs} />
            <div className="mt-2 flex justify-between items-center">
              <span className='font-normal text-xs text-left tracking-wide text-[#d32f2f]'>{formik.touched.KPIs && formik.errors.KPIs}</span>
              <span className='text-xs font-semibold' style={{ color: 'rgba(17, 29, 67, 0.4)' }}>{formik.values.KPIs?.trim()?.length}/<span>400</span> CHARACTERS</span>
            </div>
          </div>
        </section>
      </form>
      <div className="mt-5 proptech-stepper">
        <div className="sm:mb-10 mb-5 flex justify-end">
          <PTButton variant="contained" label={<span className="!font-semibold !h-5 !text-sm !leading-5 !text-center !not-italic !w-20 !text-[#111D43]">Back</span>} className='!h-12 !w-32 !mr-5 !rounded-xl !border-solid !border !border-[#111d43] !bg-[#FFFFFF]' handleOnClickEvent={() => setActiveStep(1)} />
          <PTButton variant="contained" label="Next" className='!h-12 !w-32 !rounded-xl !border-solid !bg-[#111D43]' handleOnClickEvent={(e) => companyDetails?.isPrimaryUser ? formik.submitForm(e) : setActiveStep(3)} />
        </div>
        <PTCStepper
          steps={Steps}
          activeStep={2}
          setStepper={(tab) => {
            companyDetails?.signupProgress >= tab &&
              setActiveStep(tab)
          }} />
      </div>
    </div>
  )
}

export default Heading2;
